/**
 * Wave 2 (client render): MOUNT the fitted modular weapon meshes onto the
 * player ship's hardpoints. Reads the active loadout's EffectiveShip.weapons
 * (WeaponProfile[]) and parents each weapon GLB to its resolved hardpoint node
 * (real GLB node if present, else the SKIFF_HARDPOINTS fallback transform).
 *
 * Mirrors the npcs.ts / bodyFactory.ts loader pattern: GLTFLoader + the GLB's
 * OWN matte materials (no glow added) + graceful fallback. A missing weapon GLB
 * or an unequipped hardpoint leaves a BARE EMPTY mount — never an error, never a
 * placeholder blob. Loads once and re-syncs only the deltas when the loadout
 * changes. sim-core is untouched; this is purely the render-side mapping.
 */
import * as THREE from 'three';
import { createGltfLoader } from './gltf.js';
import {
  SHIP_SKIFF,
  STARTER_MODULES,
  applyLoadout,
  type EffectiveShip,
  type HardpointId,
  type Loadout,
  type WeaponProfile,
} from '@blackmarble/sim-core';
import { normaliseToRadius, enableShadows } from './bodyFactory.js';
import { getActive } from '../sim/loadoutStore.js';
import type { PlayerShipRenderable } from './playerShip.js';

/** The weapon GLB ids shipped under assets/weapons[-dec]/<id>.glb. */
export const WEAPON_MESH_IDS = ['coilgun', 'pulse', 'railshot', 'lance', 'missile'] as const;
export type WeaponMeshId = (typeof WEAPON_MESH_IDS)[number];

const DEFAULT_WEAPON_MESH: WeaponMeshId = 'pulse';
const WEAPON_DISPLAY_KM = 1.4; // render half-extent, smaller than the 5km hull

/** Explicit mesh for each current starter catalog module id. */
const MODULE_MESH: Readonly<Record<string, WeaponMeshId>> = {
  'pulse-laser-s1': 'pulse',
  'autocannon-m2': 'railshot',
  'railgun-m2': 'coilgun', // stands in for the spec's mod-coilgun-mk1
};

/** Keyword fallback (matched against id + name) for future / renamed modules. */
const MESH_KEYWORDS: ReadonlyArray<readonly [RegExp, WeaponMeshId]> = [
  [/coil/, 'coilgun'],
  [/rail|gauss/, 'railshot'],
  [/lance|spinal/, 'lance'], // family noun wins over the generic beam/laser rule
  [/missile|torpedo|rocket/, 'missile'],
  [/pulse|laser|beam|plasma/, 'pulse'],
  [/cannon|gun|kinetic/, 'railshot'],
];

/** A fitted weapon reduced to what mesh-selection needs. */
export interface WeaponMeshKey {
  moduleId: string;
  name?: string;
}

/** Map a fitted weapon (module id + display name) to its GLB mesh id. Pure. */
export const weaponMeshId = (weapon: WeaponMeshKey): WeaponMeshId => {
  const explicit = MODULE_MESH[weapon.moduleId];
  if (explicit) return explicit;
  const haystack = `${weapon.moduleId} ${weapon.name ?? ''}`.toLowerCase();
  for (const [pattern, id] of MESH_KEYWORDS) if (pattern.test(haystack)) return id;
  return DEFAULT_WEAPON_MESH;
};

/** Full GLB url for a weapon mesh id under a resolved asset base dir. */
export const weaponMeshUrl = (id: WeaponMeshId, baseUrl: string): string =>
  `${baseUrl}${id}.glb`;

interface Mounted {
  meshId: WeaponMeshId;
  group: THREE.Group;
}

/**
 * Mounts (and re-syncs) the fitted weapon meshes on a player ship. Construct
 * once with the loaded ship + the weapons asset base dir, then call `sync` with
 * the active EffectiveShip; call `sync` again whenever the loadout changes.
 */
export class WeaponMounts {
  private readonly loader = createGltfLoader();
  private readonly mounted = new Map<HardpointId, Mounted>();

  constructor(
    private readonly ship: PlayerShipRenderable,
    private readonly baseUrl: string,
  ) {}

  /**
   * Re-mount from an explicit loadout. The fitted weapons depend only on the
   * loadout (hardpoints + fitted + catalog), so the SKIFF base spec here yields
   * the same WeaponProfile[] the flight controller fires — the mounts and the
   * guns always agree. Call when the active loadout changes (ship-builder UI /
   * loadoutStore.setActive).
   */
  setLoadout(loadout: Loadout): Promise<void> {
    return this.sync(applyLoadout(SHIP_SKIFF, loadout, STARTER_MODULES));
  }

  /** Re-mount from the persisted ACTIVE loadout (loadoutStore.getActive). */
  refresh(): Promise<void> {
    return this.setLoadout(getActive());
  }

  /** Mount the active loadout's weapons; remove meshes for emptied hardpoints. */
  async sync(effective: EffectiveShip): Promise<void> {
    const nodeNames = new Map(effective.loadout.hardpoints.map((h) => [h.id, h.nodeName]));
    const wanted = new Set(effective.weapons.map((w) => w.hardpointId));
    this.prune(wanted);
    for (const weapon of effective.weapons) {
      const nodeName = nodeNames.get(weapon.hardpointId);
      if (nodeName) await this.mountOne(weapon, nodeName);
    }
  }

  /** Remove mounted meshes whose hardpoint is no longer equipped. */
  private prune(wanted: Set<HardpointId>): void {
    for (const [hardpointId, entry] of this.mounted) {
      if (wanted.has(hardpointId)) continue;
      entry.group.removeFromParent();
      this.mounted.delete(hardpointId);
    }
  }

  private async mountOne(weapon: WeaponProfile, nodeName: string): Promise<void> {
    const meshId = weaponMeshId(weapon);
    if (this.mounted.get(weapon.hardpointId)?.meshId === meshId) return; // unchanged
    this.mounted.get(weapon.hardpointId)?.group.removeFromParent();
    const group = new THREE.Group();
    group.name = `weapon:${weapon.hardpointId}`;
    await this.loadMesh(group, meshId);
    this.ship.resolveHardpoint(nodeName).add(group);
    this.mounted.set(weapon.hardpointId, { meshId, group });
  }

  /** Load the weapon GLB (its own matte materials); leave bare on any failure. */
  private async loadMesh(group: THREE.Group, meshId: WeaponMeshId): Promise<void> {
    try {
      const gltf = await this.loader.loadAsync(weaponMeshUrl(meshId, this.baseUrl));
      normaliseToRadius(gltf.scene, WEAPON_DISPLAY_KM);
      enableShadows(gltf.scene); // WAVE 2a: mounted weapons rake shadow across the hull
      group.add(gltf.scene);
    } catch (err) {
      console.warn(`[assets] weapon GLB missing (${meshId}) — bare mount`, err);
    }
  }
}
